import React from 'react';
import { when } from 'mobx';
import { observer } from 'mobx-react';
import * as Sentry from '@sentry/browser';
import classnames from 'classnames';

import HeaderLogoAndDropDown from '../headerComponents/HeaderLogoAndDropDown';
import CookieConsent from '../generalComponents/CookieConsent';
import MobileHeaderNav from '../headerComponents/MobileHeaderNav/MobileHeaderNav';
import AnchorDrawerNavigation from '../headerComponents/AnchorDrawerNavigation';
import HeaderNav from '../headerComponents/HeaderNav';
import AnchorPortalHeader from '../headerComponents/AnchorPortalHeader';
import UpgradeAccountHeader from '../UpgradeAccountHeader/UpgradeAccountHeader';
import { UserContext } from '../../context';
import SaasLogger from '../../services/SaasLogger';
import SummerPackModal from '../SummerPackModal/SummerPackModal';
import NNLiveModal from '../NNLiveModal/NNLiveModal';
import AnchorStore from '../../store/AnchorStore';
import { getCookie } from '../../utils/helpers';
import { SUMMER_PACK, NNLIVE, CALENDAR_DISPLAY } from '../../constants/modals';
import SummerPackStore from '../../store/SummerPackStore';
import NNLiveStore from '../../store/NNLiveStore';
import EmailWhitelistMessage from '../generalComponents/EmailWhitelistMessage';
import CalendarPromoModal from '../CalendarPromoModal/CalendarPromoModal';


@observer
class Header extends React.Component {

  static contextType = UserContext;

  state = {
    showMobileHeader: false,
    isMobile: false,
    showAnchorMobileHeader: false,
    showCookieConsent: !document.cookie.includes('notify_cookie_usage=true'),
    showCalendarModal: false,
    showSummerPackModal: false,
    showNNLiveModal: false,
    showEmailWhitelist: !document.cookie.includes('show_email_whitelist=true'),
  };

  toggleMobileHeader = () => {
    const { showMobileHeader } = this.state;
    this.setState({ showMobileHeader: !showMobileHeader });
  };

  openAnchorMobileHeader = () => this.setState({ showAnchorMobileHeader: true });
  closeAnchorMobileHeader = () => this.setState({ showAnchorMobileHeader: false });

  closeCalendarModal = () => this.setState({ showCalendarModal: false });

  closeSummerPackModal = () => this.setState({ showSummerPackModal: false });

  closeNNLiveModal = () => this.setState({ showNNLiveModal: false });

  componentDidMount() {
    this.setState({ isMobile: window.innerWidth <= 991 });
    this.setUserData();
    window.addEventListener('resize', () => {
      if (this.state.isMobile !== (window.innerWidth <= 991)) {
        this.setState({ isMobile: window.innerWidth <= 991 });
      }
    }, false);
    this.handleSummerPackModal();
    when(
      () => AnchorStore.isAnchorLive !== undefined,
      () => {
        this.handleSummerPackModal();
      }
    );

  }

  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.setState({ showCalendarModal: false });
      clearTimeout(this.timeout);
      this.handleSummerPackModal();
    }
  }

  componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  handleCalendarPromoModal = () => {
    const user = this.context;
    if (!user?.features.includes('CALENDAR_2021') && !user?.features.includes('SHOW_SHOP_LINK')) {
      return;
    }
    this.timeout = setTimeout(() => {
      const { location: { pathname }, modalsData } = this.props;
      const modal = modalsData.find((el) => el.modalType === CALENDAR_DISPLAY);
      const exceptions = ['/account/purchase', '/calendar-purchase', '/calendar-checkout', '/oauth2/login'];
      const calendarModalShown = sessionStorage.getItem('CALENDAR_MODAL_SHOWN');
      const shouldntShowModal =
        exceptions.includes(pathname) ||
        (/(^\/naked-news-anchor-)/.test(pathname) && (AnchorStore.isAnchorLive || AnchorStore.isAnchorLive === undefined)) ||
        (!user.authenticated && getCookie(CALENDAR_DISPLAY)) ||
        (user.authenticated && modal && !modal.showModal) || calendarModalShown;


      if (!shouldntShowModal) {
        this.setState({ showCalendarModal: true });
        sessionStorage.setItem('CALENDAR_MODAL_SHOWN', true);
      }
    }, 5000);
  };

  handleSummerPackModal = () => {
    const user = this.context;
    const hasCF = user.features.includes('SUMMER_PACK_2021');
    const { modalDismissed } = SummerPackStore;
    if (!hasCF) {
      return;
    }
    this.timeout = setTimeout(() => {
      const { location: { pathname }, modalsData } = this.props;
      const modal = modalsData.find((el) => el.modalType === SUMMER_PACK);
      const exceptions = ['/account/purchase', '/summerpack-purchase', '/oauth2/login'];
      const shouldntShowSummerpackModal =
        modalDismissed ||
        exceptions.includes(pathname) ||
        (/(^\/naked-news-anchor-)/.test(pathname) && (AnchorStore.isAnchorLive || AnchorStore.isAnchorLive === undefined)) ||
        (!user.authenticated && getCookie(SUMMER_PACK)) ||
        (user.authenticated && modal && !modal.showModal);

      if (!shouldntShowSummerpackModal) {
        this.setState({ showSummerPackModal: true });
      }
    }, 5000);
  };

  handleNNLiveModal = () => {
    const user = this.context;
    const { modalDismissed } = NNLiveStore;

    this.timeout = setTimeout(() => {
      const { location: { pathname }, modalsData } = this.props;
      const modal = modalsData.find((el) => el.modalType === NNLIVE);
      const exceptions = ['/account/purchase', '/summerpack-purchase', '/oauth2/login', '/live'];
      const shouldntShowNNLiveModal =
        modalDismissed ||
        exceptions.includes(pathname) ||
        (/(^\/naked-news-anchor-)/.test(pathname) && (AnchorStore.isAnchorLive || AnchorStore.isAnchorLive === undefined)) ||
        (!user.authenticated && getCookie(NNLIVE)) ||
        (user.authenticated && (!!user.user.account.anchorSubscriptions.find((obj) => obj.anchorName === 'Naked News Live'))) ||
        (user.authenticated && modal && !modal.showModal);

      if (!shouldntShowNNLiveModal) {
        this.setState({ showNNLiveModal: true });
      }
    }, 5000);
  };

  setUserData = () => {
    const user = this.context;
    if (!user.authenticated) {
      return;
    }
    const userData = {
      id: user.user.account.id,
      username: user.user.username,
    };
    if (user.authenticated) {
      Sentry.configureScope((scope) => {
        scope.setUser(userData);
      });
      SaasLogger.setUserData(userData);
    }
  };

  isAnchor = () => {
    const user = this.context;
    let isAnchor = false;
    if (user && user.user && user.user.username) {
      isAnchor = user.user.account.anchor;
    }

    return isAnchor;
  };

  acceptCookies = (event) => {
    event.preventDefault();
    document.cookie = 'notify_cookie_usage=true;max-age=2592000';
    this.setState({ showCookieConsent: false });
  };

  dontShow = () => {
    document.cookie = 'show_email_whitelist=true;max-age=2592000';
    this.removeEmailWhitelistMessage();
  };

  removeEmailWhitelistMessage = () => {
    this.setState({ showEmailWhitelist: false });
  };

  render() {
    const {
      showPremiumUpgradeBar,
      closePremiumUpgradeBar,
      userPremiumExpired,
      isAnchorPortal,
      location,
      activeCampaign,
      isAffiliatePage,
    } = this.props;
    const { location: { pathname } } = this.props;
    const hideHeaderLoginButton = pathname === '/oauth2/login';
    const user = this.context;
    const {
      showMobileHeader,
      isMobile,
      showAnchorMobileHeader,
      showCookieConsent,
      showCalendarModal,
      showSummerPackModal,
      showNNLiveModal,
      showEmailWhitelist,
    } = this.state;
    const alreadyMember = document.cookie.includes('_membership=1');
    if (location.pathname === '/dashboard/broadcast' && isMobile) {
      return null;
    }

    const isFan = user.authenticated && !user.user.account.anchor;

    return (
      <>
        {showEmailWhitelist && isFan && (
          <EmailWhitelistMessage
            dontShow={this.dontShow}
            removeEmailWhitelistMessage={this.removeEmailWhitelistMessage}
          />
        )}
        {showCookieConsent && <CookieConsent onAccept={this.acceptCookies} />}
        {showPremiumUpgradeBar && (
          <UpgradeAccountHeader
            closePremiumUpgradeBar={closePremiumUpgradeBar}
            isMobile={isMobile}
          />
        )}
        <CalendarPromoModal
          closeCalendarModal={this.closeCalendarModal}
          showCalendarModal={showCalendarModal}
          user={user}
        />
        <SummerPackModal
          closeSummerPackModal={this.closeSummerPackModal}
          showSummerPackModal={showSummerPackModal}
          user={user}
        />
        <NNLiveModal
          closeNNLiveModal={this.closeNNLiveModal}
          showNNLiveModal={showNNLiveModal}
          user={user}
        />
        <div id="navigation">
          <div
            className={classnames({
              'navbar': true,
              'navbar-lg': true,
              'navbar-topnav': true,
              'anchor-portal-topnav': isAnchorPortal,
              'affiliate-topnav': isAffiliatePage,
            })}
            id="header-nav"
            role="navigation"
          >
            {isAnchorPortal ? (
              <>
                <AnchorPortalHeader handleClick={this.openAnchorMobileHeader} />
                <AnchorDrawerNavigation
                  anchor="right"
                  open={showAnchorMobileHeader}
                  onClose={this.closeAnchorMobileHeader}
                />
              </>
            ) : (
              <>
                <HeaderLogoAndDropDown
                  activeCampaign={activeCampaign}
                  location={location}
                  toggleMobileHeader={this.toggleMobileHeader}
                  user={user}
                  userPremiumExpired={userPremiumExpired}
                />
                <MobileHeaderNav
                  activeCampaign={activeCampaign}
                  alreadyMember={alreadyMember}
                  hideHeaderLoginButton={hideHeaderLoginButton}
                  isAffiliatePage={isAffiliatePage}
                  isAnchor={this.isAnchor()}
                  isMobile={isMobile}
                  location={this.props.location}
                  showMobileHeader={showMobileHeader}
                  user={user}
                  userPremiumExpired={userPremiumExpired}
                />
                <HeaderNav isAnchor={this.isAnchor()} user={user} />
              </>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default Header;
